import { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Users, 
  FileText, 
  Settings, 
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  X,
  Building2,
  ShoppingCart,
  Landmark,
  BarChart3,
  Wallet,
  FileScan,
  Puzzle,
  CreditCard,
  Package,
  Factory,
  Truck,
  FolderKanban,
  Wrench,
  Globe,
  FolderOpen,
  MessagesSquare,
  CalendarDays,
  Video,
  Mail,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useUIStore } from '@/store/uiStore';
import { Button } from '@/components/ui/Button';

interface MenuItem {
  label: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  children?: { label: string; href: string }[];
}

const menuItems: MenuItem[] = [
  {
    label: 'Dashboard',
    href: '/dashboard',
    icon: LayoutDashboard,
  },
  {
    label: 'Chat',
    href: '/chat',
    icon: MessagesSquare,
  },
  {
    label: 'Email',
    href: '/email',
    icon: Mail,
  },
  {
    label: 'Calendar',
    href: '/calendar',
    icon: CalendarDays,
  },
  {
    label: 'Meetings',
    href: '/meetings',
    icon: Video,
  },
  {
    label: 'Contacts',
    href: '/contacts',
    icon: Users,
    children: [
      { label: 'All Contacts', href: '/contacts' },
      { label: 'Add Contact', href: '/contacts/new' },
      { label: 'Contact Map', href: '/contacts/map' },
    ],
  },
  {
    label: 'Sales',
    href: '/invoices',
    icon: FileText,
    children: [
      { label: 'Leads', href: '/sales/leads' },
      { label: 'Quotes', href: '/quotes' },
      { label: 'Sales Orders', href: '/sales-orders' },
      { label: 'Invoices', href: '/invoices' },
      { label: 'Create Invoice', href: '/invoices/new' },
      { label: 'Recurring Invoices', href: '/recurring-invoices' },
      { label: 'Credit Notes', href: '/credit-notes' },
      { label: 'Price Lists', href: '/sales/price-lists' },
    ],
  },
  {
    label: 'Purchases',
    href: '/bills',
    icon: ShoppingCart,
    children: [
      { label: 'Bills', href: '/bills' },
      { label: 'Purchase Orders', href: '/purchase-orders' },
      { label: 'Recurring', href: '/purchases/recurring' },
      { label: 'Suppliers', href: '/contacts?type=supplier' },
    ],
  },
  {
    label: 'Payments',
    href: '/payments',
    icon: Wallet,
    children: [
      { label: 'All Payments', href: '/payments' },
      { label: 'Record Payment', href: '/payments/new' },
    ],
  },
  {
    label: 'Banking',
    href: '/bank-accounts',
    icon: Landmark,
    children: [
      { label: 'Bank Accounts', href: '/bank-accounts' },
      { label: 'Reconciliation', href: '/banking/reconciliation' },
      { label: 'Bank Feeds', href: '/banking/bank-feeds' },
      { label: 'Bank Rules', href: '/banking/bank-rules' },
    ],
  },
  {
    label: 'Inventory',
    href: '/stock/products',
    icon: Package,
    children: [
      { label: 'Products', href: '/stock/products' },
      { label: 'Stock Levels', href: '/stock/levels' },
      { label: 'Warehouses', href: '/stock/warehouses' },
      { label: 'Movements', href: '/stock/movements' },
      { label: 'Price History', href: '/stock/price-history' },
    ],
  },
  {
    label: 'Production',
    href: '/production/batches',
    icon: Factory,
    children: [
      { label: 'Batches', href: '/production/batches' },
      { label: 'Bill of Materials', href: '/production/bom' },
    ],
  },
  {
    label: 'Shipping',
    href: '/shipping/shipments',
    icon: Truck,
    children: [
      { label: 'Shipments', href: '/shipping/shipments' },
      { label: 'Containers', href: '/shipping/containers' },
    ],
  },
  {
    label: 'Projects',
    href: '/projects',
    icon: FolderKanban,
    children: [
      { label: 'All Projects', href: '/projects' },
      { label: 'Time Tracking', href: '/projects/time' },
    ],
  },
  {
    label: 'Assets',
    href: '/assets',
    icon: Wrench,
    children: [
      { label: 'Management', href: '/assets' },
      { label: 'Usage', href: '/assets/usage' },
      { label: 'Maintenance', href: '/assets/maintenance' },
    ],
  },
  {
    label: 'FX',
    href: '/fx/transfers',
    icon: Globe,
    children: [
      { label: 'Transfers', href: '/fx/transfers' },
      { label: 'Beneficiaries', href: '/fx/beneficiaries' },
    ],
  },
  {
    label: 'Documents',
    href: '/documents',
    icon: FileScan,
  },
  {
    label: 'Files',
    href: '/files',
    icon: FolderOpen,
  },
  {
    label: 'Integrations',
    href: '/integrations',
    icon: Puzzle,
  },
  {
    label: 'Payroll',
    href: '/payroll/employees',
    icon: CreditCard,
    children: [
      { label: 'Employees', href: '/payroll/employees' },
      { label: 'Pay Runs', href: '/payroll/pay-runs' },
      { label: 'STP Submissions', href: '/payroll/stp' },
      { label: 'Superannuation', href: '/payroll/super/funds' },
      { label: 'Leave', href: '/payroll/leave' },
      { label: 'Timesheets', href: '/payroll/timesheets' },
      { label: 'ABA Files', href: '/payroll/aba' },
      { label: 'Expense Claims', href: '/payroll/expense-claims' },
      { label: 'Payroll Reports', href: '/payroll/reports' },
    ],
  },
  {
    label: 'Reports',
    href: '/reports',
    icon: BarChart3,
    children: [
      { label: 'Financial Reports', href: '/reports' },
      { label: 'Profit & Loss', href: '/reports/profit-loss' },
      { label: 'Balance Sheet', href: '/reports/balance-sheet' },
      { label: 'GST Reports', href: '/reports/gst' },
      { label: 'Chart of Accounts', href: '/reports/chart-of-accounts' },
      { label: 'Trial Balance', href: '/reports/trial-balance' },
      { label: 'Manual Journals', href: '/reports/journals' },
      { label: 'Budgets', href: '/reports/budgets' },
      { label: 'Account Transactions', href: '/reports/account-transactions' },
      { label: 'GST Detail', href: '/reports/gst-detail' },
      { label: 'Sales Summary', href: '/reports/sales-summary' },
      { label: 'Purchases Summary', href: '/reports/purchases-summary' },
      { label: 'Inventory Valuation', href: '/reports/inventory-valuation' },
    ],
  },
  {
    label: 'Settings',
    href: '/settings',
    icon: Settings,
    children: [
      { label: 'General', href: '/settings' },
      { label: 'Branding', href: '/settings/branding' },
      { label: 'Templates', href: '/settings/templates' },
      { label: 'Automation', href: '/settings/automation' },
      { label: 'Import / Export', href: '/settings/import-export' },
      { label: 'Activity Log', href: '/settings/activity-log' },
    ],
  },
];

function MenuItemComponent({
  item,
  collapsed,
  isOpen,
  onToggle,
}: {
  item: MenuItem;
  collapsed: boolean;
  isOpen: boolean;
  onToggle: () => void;
}) {
  const location = useLocation();
  const hasChildren = item.children && item.children.length > 0;
  const isActive = location.pathname === item.href || 
    (hasChildren && item.children?.some(child => location.pathname === child.href || location.pathname.startsWith(child.href)));

  if (collapsed) {
    return (
      <div className="group relative">
        <NavLink
          to={item.href}
          className={cn(
            'flex items-center justify-center p-3 rounded-lg transition-all duration-200',
            isActive 
              ? 'bg-primary/10 text-primary' 
              : 'text-muted-foreground hover:bg-accent hover:text-foreground'
          )}
          title={item.label}
        >
          <item.icon className="h-5 w-5" />
        </NavLink>
        
        {/* Tooltip */}
        <div className="absolute left-full ml-2 px-2 py-1 bg-popover text-popover-foreground text-sm rounded-md opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all whitespace-nowrap z-50">
          {item.label}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div
        className={cn(
          'relative w-full flex items-center justify-between rounded-lg transition-all duration-200',
          isActive 
            ? 'bg-primary/10 text-primary font-medium' 
            : 'text-muted-foreground hover:bg-accent hover:text-foreground'
        )}
      >
        {isActive && (
          <span className="absolute left-0 top-1/2 -translate-y-1/2 h-5 w-0.5 rounded-full bg-primary" />
        )}
        {/* Main row navigates to the section's page */}
        <NavLink to={item.href} className="flex items-center gap-3 px-3 py-2 flex-1 min-w-0">
          <item.icon className="h-5 w-5 shrink-0" />
          <span className="text-sm font-medium truncate">{item.label}</span>
        </NavLink>
        {/* Chevron toggles expansion independently */}
        {hasChildren && (
          <button
            onClick={(e) => {
              e.preventDefault();
              onToggle();
            }}
            className="px-2 py-2 rounded-r-lg hover:bg-accent/60"
            aria-label={isOpen ? `Collapse ${item.label}` : `Expand ${item.label}`}
          >
            <ChevronDown 
              className={cn(
                'h-4 w-4 transition-transform duration-200',
                isOpen && 'rotate-180'
              )} 
            />
          </button>
        )}
      </div>
      
      {hasChildren && isOpen && (
        <div className="mt-1 ml-4 pl-4 border-l border-border space-y-1">
          {item.children?.map((child) => (
            <NavLink
              key={child.href}
              to={child.href}
              className={({ isActive }) => cn(
                'block px-3 py-1.5 rounded-md text-sm transition-all duration-200',
                isActive 
                  ? 'text-primary font-medium bg-primary/5' 
                  : 'text-muted-foreground hover:text-foreground hover:bg-accent/50'
              )}
            >
              {child.label}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

export function Sidebar() {
  const { 
    sidebarCollapsed, 
    toggleSidebar, 
    mobileSidebarOpen, 
    setMobileSidebarOpen 
  } = useUIStore();
  const location = useLocation();

  // Accordion: one section open at a time; the section containing the
  // active route always stays open.
  const activeSection = menuItems.find(
    (item) =>
      item.children?.some(
        (child) =>
          location.pathname === child.href ||
          location.pathname.startsWith(child.href + '/')
      ) || location.pathname === item.href
  )?.href;
  const [openSection, setOpenSection] = useState<string | null>(activeSection || null);

  useEffect(() => {
    if (activeSection) setOpenSection(activeSection);
  }, [activeSection]);

  const renderMenuItems = (collapsed: boolean) =>
    menuItems.map((item) => (
      <MenuItemComponent
        key={item.href}
        item={item}
        collapsed={collapsed}
        isOpen={openSection === item.href}
        onToggle={() => setOpenSection(openSection === item.href ? null : item.href)}
      />
    ));

  return (
    <>
      {/* Mobile Sidebar Overlay */}
      {mobileSidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setMobileSidebarOpen(false)}
        />
      )}

      {/* Mobile Sidebar */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 w-64 bg-card border-r border-border transform transition-transform duration-300 ease-in-out lg:hidden flex flex-col',
          mobileSidebarOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="flex items-center justify-between p-4 border-b border-border shrink-0">
          <div className="flex items-center gap-2">
            <Building2 className="h-6 w-6 text-primary" />
            <span className="font-semibold text-lg tracking-tight">AxiomVanta</span>
          </div>
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => setMobileSidebarOpen(false)}
          >
            <X className="h-5 w-5" />
          </Button>
        </div>
        
        <nav className="p-4 space-y-2 flex-1 overflow-y-auto">
          {renderMenuItems(false)}
        </nav>
      </aside>

      {/* Desktop Sidebar */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-40 bg-card border-r border-border transition-all duration-300 ease-in-out hidden lg:flex lg:flex-col',
          sidebarCollapsed ? 'w-20' : 'w-64'
        )}
      >
        {/* Logo Section */}
        <div className={cn(
          'flex items-center border-b border-border shrink-0',
          sidebarCollapsed ? 'justify-center p-4' : 'justify-between p-4'
        )}>
          <div className="flex items-center gap-2">
            <Building2 className="h-6 w-6 text-primary flex-shrink-0" />
            {!sidebarCollapsed && (
              <span className="font-semibold text-lg tracking-tight truncate">
                AxiomVanta
              </span>
            )}
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleSidebar}
            className={cn(
              'h-8 w-8',
              sidebarCollapsed && 'hidden'
            )}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
        </div>

        {/* Toggle Button (when collapsed) */}
        {sidebarCollapsed && (
          <div className="flex justify-center py-2 border-b border-border shrink-0">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleSidebar}
              className="h-8 w-8"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}

        {/* Navigation — scrolls when content exceeds the viewport */}
        <nav className={cn(
          'space-y-2 flex-1 overflow-y-auto',
          sidebarCollapsed ? 'p-2' : 'p-4'
        )}>
          {renderMenuItems(sidebarCollapsed)}
        </nav>
      </aside>
    </>
  );
}
